import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { CircularProgress, Box, Typography, Alert } from '@mui/material';
import oidcAuth from '../../services/auth/oidc-client';
import { loginSuccess, loginFailure } from '../../store/authSlice';

const normalizeBasePath = (value?: string | null): string => {
  if (!value || value === '/' || value === './') {
    return '';
  }
  const trimmed = value.replace(/^\/+|\/+$/g, '');
  return trimmed ? `/${trimmed}` : '';
};

const BASE_PATH = normalizeBasePath(import.meta.env.BASE_URL as string | undefined);
const ROOT_ROUTE = BASE_PATH || '/';
const LOGIN_ROUTE = `${BASE_PATH}/login`.replace('//', '/');

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);
  const hasRunRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (hasRunRef.current) return;
      hasRunRef.current = true;
      try {
        const user = await oidcAuth.handleCallback();
        if (user) {
          dispatch(
            loginSuccess({
              accessToken: user.access_token,
              refreshToken: user.refresh_token || '',
              role: (user.profile as any).roles?.[0] || 'user',
              user: {
                id: user.profile.sub || '',
                username: (user.profile as any).preferred_username || user.profile.email || '',
                email: user.profile.email || '',
                name: (user.profile as any).name || '',
                roles: (user.profile as any).roles || [],
              },
            }),
          );
          const returnUrl =
            (user.state as any)?.returnUrl && typeof (user.state as any)?.returnUrl === 'string'
              ? (user.state as any).returnUrl
              : ROOT_ROUTE;
          navigate(returnUrl, { replace: true });
        }
      } catch (err) {
        console.error('Authentication callback error:', err);
        setError('Authentication failed. Please try again.');
        dispatch(loginFailure('Authentication callback failed'));
        setTimeout(() => navigate(LOGIN_ROUTE, { replace: true }), 2000);
      }
    };

    handleCallback();
  }, [navigate, dispatch]);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh">
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : (
        <>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Completing authentication...
          </Typography>
        </>
      )}
    </Box>
  );
};

export default AuthCallback;

